import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { SubCompanyCard } from "@/components/subs/sub-company-card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ClipboardCheck, FolderKanban, UserPlus, StickyNote, X, AlertTriangle, Briefcase, Award, Bell, BookOpen, Clock, HelpCircle, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { apiGet, apiPost } from "@/lib/api";
import { useFlhaWizardStore } from "@/stores/flha-wizard-store";
import { useFlhaLabel } from "@/hooks/use-flha-label";
import { useWeather } from "@/hooks/use-weather";
import {
  useCurrentProject,
  writeStoredProjectId,
} from "@/hooks/use-current-project";
import type { FlhaSummary } from "@/components/flha/flha-list-card";
import { AwaitingMySignature } from "@/components/home/awaiting-my-signature";
import { HomeGreeting } from "@/components/home/home-greeting";
import { PrimaryFlhaCard } from "@/components/home/primary-flha-card";
import { FlhaComingSoonCard } from "@/components/home/flha-coming-soon-card";
import { QuickActionGrid, getComingSoonPaths, COMING_SOON_PATHS } from "@/components/home/quick-action-grid";
import { StatusRow } from "@/components/home/status-row";
import { RecentFlhasSection } from "@/components/home/recent-flhas-section";
import { SectionHeader } from "@/components/home/section-header";

/** Renders a <Link> for super admin; a non-navigating <div> with a "Soon" badge for all others on Coming Soon paths. Always shows the badge for coming-soon paths regardless of role. */
function CsLink({
  to,
  role,
  children,
  className,
  onClick,
}: {
  to: string;
  role?: string | null;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const isComingSoon = COMING_SOON_PATHS.has(to);
  const disabled = getComingSoonPaths(role).has(to);
  const badge = isComingSoon ? (
    <span className="absolute right-2 top-2 z-10 text-[9px] font-bold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap pointer-events-none">
      Soon
    </span>
  ) : null;
  if (disabled) {
    return (
      <div className={className ? `${className} relative` : "relative"}>
        {badge}
        {children}
      </div>
    );
  }
  return (
    <Link to={to} className={className ? `${className} relative` : "relative"} onClick={onClick}>
      {badge}
      {children}
    </Link>
  );
}

export const Route = createFileRoute("/_authenticated/")({
  component: HomePage,
});

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

interface ProjectSummary {
  id: string;
  name: string;
  client?: string | null;
  status: string;
}

interface ProjectsResponse {
  data: ProjectSummary[];
  total: number;
  page: number;
  limit: number;
}

function HomePage() {
  const { user } = Route.useRouteContext();
  const greeting = getGreeting();
  const role = user.role || "field_personnel";

  if (role === "admin" || role === "div_admin") {
    return <AdminHome user={user} greeting={greeting} />;
  }

  if (role === "contractor") {
    return <ContractorHome user={user} greeting={greeting} />;
  }

  return <FieldTechHome user={user} greeting={greeting} />;
}

/**
 * Shared hook: start or resume the FLHA wizard.
 *
 * Preserves the exact sequence from the pre-Phase-2 implementation:
 *   1. reset() the wizard store
 *   2. remove the legacy localStorage draft key
 *   3. navigate to /flha/new-v2 (resolves via _wizard layout)
 *
 * Also persists the last-used project id so the next home visit
 * can surface the same project in the PrimaryFlhaCard title.
 */
function useStartFlha() {
  const navigate = useNavigate();
  return (projectIdToRemember?: string | null) => {
    if (projectIdToRemember) {
      writeStoredProjectId(projectIdToRemember);
    }
    useFlhaWizardStore.getState().reset();
    localStorage.removeItem("flha-wizard-draft");
    navigate({ to: "/flha/new-v2" });
  };
}

/**
 * Draft detection hook used by the primary card.
 * Reuses the same queryKey ["flha","draft"] + same /api/flha?status=draft
 * call as before — no new data flow.
 */
function useDraftInfo() {
  const { data } = useQuery({
    queryKey: ["flha", "draft"],
    queryFn: () =>
      apiGet<{ data: FlhaSummary[]; total: number }>(
        "/api/flha?status=draft&limit=3"
      ),
  });
  const count = data?.total ?? 0;
  // Read live wizard store values for the resume badge. This is a
  // reactive subscription so if the user creates a draft elsewhere
  // the card updates automatically.
  const currentStep = useFlhaWizardStore((s) => s.currentStep);
  const totalSteps = useFlhaWizardStore((s) => s.totalSteps);
  return { count, currentStep, totalSteps };
}

/** Field Tech: Primary FLHA + Quick actions + Status + Projects + Recent */
function FieldTechHome({
  user,
  greeting,
}: {
  user: { name: string; role?: string | null };
  greeting: string;
}) {
  const { data: projectsData, isLoading } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () => apiGet<ProjectsResponse>("/api/projects?status=active"),
  });
  const projects = projectsData?.data || [];

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <HomeGreeting
        name={user.name}
        role={user.role}
        greeting={greeting}
      />

      <AwaitingMySignature />

      <ProfileIncompleteWarning />

      <AdminNotesBanner />
      <SystemNotificationsBanner />
      <AssignmentsBanner />

      {user.role === "subcontractor" && <SubCompanyCard />}

      <FlhaComingSoonCard />

      <QuickActionGrid variant="field" role={user.role} />

      <StatusRow
        crewCompletion={null}
        openActions={null}
        certsExpiring={null}
        role={user.role}
      />

      {/* Your Projects */}
      <div>
        <SectionHeader>Your Projects</SectionHeader>
        {isLoading ? (
          <Card>
            <CardContent className="py-8">
              <div className="space-y-2">
                <div className="h-4 animate-pulse rounded bg-muted" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
              </div>
            </CardContent>
          </Card>
        ) : projects.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8 text-center">
              <FolderKanban className="mb-2 h-10 w-10 text-text-tertiary/40" />
              <p className="text-sm text-text-tertiary">
                You haven't been assigned to any projects yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {projects.slice(0, 5).map((p) => (
              <CsLink
                key={p.id}
                to="/projects"
                role={user.role}
                onClick={() => writeStoredProjectId(p.id)}
              >
                <Card className="cursor-pointer transition-all duration-75 hover:bg-muted/30 active:scale-[0.98] active:bg-muted/60">
                  <CardContent className="flex min-h-[56px] items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{p.name}</p>
                      {p.client && (
                        <p className="text-xs text-text-tertiary">
                          {p.client}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-text-tertiary" />
                  </CardContent>
                </Card>
              </CsLink>
            ))}
            {projects.length > 5 && (
              <p className="text-center text-xs text-text-tertiary">
                + {projects.length - 5} more project
                {projects.length - 5 !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        )}
      </div>

      <RecentFlhasSection />
    </div>
  );
}

/** Contractor (GC/CSO): read-only access to documents, certifications, incident reporting */
function ContractorHome({
  user,
  greeting,
}: {
  user: { name: string; role?: string | null };
  greeting: string;
}) {
  return (
    <div className="mx-auto max-w-lg space-y-6">
      <HomeGreeting name={user.name} role={user.role} greeting={greeting} />

      <AwaitingMySignature />
      <ProfileIncompleteWarning />
      <AdminNotesBanner />
      <SystemNotificationsBanner />
      <AssignmentsBanner />
      <FlhaComingSoonCard />
      <QuickActionGrid variant="contractor" role={user.role} reviewBadge={null} />
    </div>
  );
}

/** Admin: Primary FLHA + Quick actions (lead) + Stats + Manage Projects + Recent */
function AdminHome({
  user,
  greeting,
}: {
  user: { name: string; role?: string | null };
  greeting: string;
}) {
  const { data: projectsData } = useQuery({
    queryKey: ["projects", "all"],
    queryFn: () => apiGet<ProjectsResponse>("/api/projects?status=all"),
  });

  const { data: pendingData } = useQuery({
    queryKey: ["supervisor-queue-count"],
    queryFn: () =>
      apiGet<{ data: unknown[]; total: number }>(
        "/api/flha?status=submitted&limit=1"
      ),
  });

  const { data: pendingApprovalsData } = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => apiGet<{ data: unknown[] }>("/api/approvals/pending"),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: pendingTitleRequestsData } = useQuery({
    queryKey: ["admin", "job-title-requests"],
    queryFn: () => apiGet<{ data: unknown[] }>("/api/admin/job-titles/requests"),
    refetchInterval: 60_000,
  });

  const { data: pendingCertReviewData } = useQuery({
    queryKey: ["admin", "cert-review"],
    queryFn: () => apiGet<{ data: unknown[] }>("/api/admin/certifications/review"),
    refetchInterval: 60_000,
  });

  const totalProjects = projectsData?.total ?? 0;
  const pendingCount = pendingData?.total ?? 0;
  const pendingApprovals = pendingApprovalsData?.data?.length ?? 0;
  const pendingTitleRequests = pendingTitleRequestsData?.data?.length ?? 0;
  const pendingCertReviews = pendingCertReviewData?.data?.length ?? 0;

  const { project } = useCurrentProject();
  const { data: weather } = useWeather();
  const label = useFlhaLabel();
  const startFlha = useStartFlha();
  const draftInfo = useDraftInfo();
  const hasDraft = draftInfo.count > 0;

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <HomeGreeting
        name={user.name}
        role={user.role}
        greeting={greeting}
      />

      <AwaitingMySignature />

      <ProfileIncompleteWarning />

      <AdminNotesBanner />
      <NeedsClarificationBanner />
      <SystemNotificationsBanner />
      <AssignmentsBanner />

      {/* ── Priority warnings ───────────────────────────────────────── */}

      {/* 1. Pending worker account approvals */}
      {pendingApprovals > 0 && (
        <Link to="/admin/users">
          <Card className="cursor-pointer border border-amber-500/40 bg-amber-500/10 transition-all duration-75 hover:bg-amber-500/20 active:scale-[0.98]">
            <CardContent className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                <UserPlus className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                  {pendingApprovals} worker{pendingApprovals !== 1 ? "s" : ""} awaiting approval
                </p>
                <p className="text-xs text-amber-600/80 dark:text-amber-500/80">
                  Tap to review pending workers
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            </CardContent>
          </Card>
        </Link>
      )}

      {/* 2. Pending custom job title requests */}
      {pendingTitleRequests > 0 && (
        <Link to="/admin/job-titles">
          <Card className="cursor-pointer border border-red-400/40 bg-red-500/10 transition-all duration-75 hover:bg-red-500/20 active:scale-[0.98]">
            <CardContent className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500/20">
                <Briefcase className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                  {pendingTitleRequests} job title request{pendingTitleRequests !== 1 ? "s" : ""} pending
                </p>
                <p className="text-xs text-red-600/80 dark:text-red-500/80">
                  Tap to review custom job title requests
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
            </CardContent>
          </Card>
        </Link>
      )}

      {/* 3. Pending custom credential reviews */}
      {pendingCertReviews > 0 && (
        <Link to="/admin/certifications" search={{ q: undefined, assignmentId: undefined, workerId: undefined }}>
          <Card className="cursor-pointer border border-red-400/40 bg-red-500/10 transition-all duration-75 hover:bg-red-500/20 active:scale-[0.98]">
            <CardContent className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500/20">
                <Award className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                  {pendingCertReviews} credential request{pendingCertReviews !== 1 ? "s" : ""} pending
                </p>
                <p className="text-xs text-red-600/80 dark:text-red-500/80">
                  Tap to review custom credential submissions
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
            </CardContent>
          </Card>
        </Link>
      )}

      {user.role === "admin" ? (
        <PrimaryFlhaCard
          projectName={project?.name ?? null}
          weather={weather ?? null}
          draftStep={hasDraft ? draftInfo.currentStep : null}
          draftTotalSteps={hasDraft ? draftInfo.totalSteps : null}
          singularLabel={label.singular}
          onStart={() => startFlha(project?.id ?? null)}
        />
      ) : (
        <FlhaComingSoonCard />
      )}

      <QuickActionGrid variant="lead" reviewBadge={pendingCount} role={user.role} />

      <StatusRow
        crewCompletion={null}
        openActions={null}
        certsExpiring={null}
        role={user.role}
      />

      {/* Quick stats */}
      {(() => {
        const isSuperAdmin = user.role === "admin";
        const reviewDisabled = !isSuperAdmin;
        return (
          <div className="grid grid-cols-2 gap-3">
            <Card className="border-l-4 border-l-slate-700">
              <CardContent className="px-4 py-4 text-center">
                <FolderKanban className="mx-auto mb-1 h-6 w-6 text-slate-700" />
                <p className="font-display text-2xl font-bold text-text-primary">
                  {totalProjects}
                </p>
                <p className="text-xs text-text-tertiary">Projects</p>
              </CardContent>
            </Card>
            {reviewDisabled ? (
              <Card className="relative h-full border-l-4 border-l-paris-red">
                <span className="absolute right-2 top-2 text-[9px] font-bold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">Soon</span>
                <CardContent className="px-4 py-4 text-center">
                  <ClipboardCheck className="mx-auto mb-1 h-6 w-6 text-text-tertiary" />
                  <p className="font-display text-2xl font-bold text-text-tertiary">{pendingCount}</p>
                  <p className="text-xs text-text-tertiary">Pending Reviews</p>
                </CardContent>
              </Card>
            ) : (
              <Link to="/review">
                <Card className="relative h-full cursor-pointer border-l-4 border-l-paris-red transition-all duration-75 hover:bg-muted/30 active:scale-[0.98] active:bg-muted/60">
                  <span className="absolute right-2 top-2 text-[9px] font-bold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">Soon</span>
                  <CardContent className="px-4 py-4 text-center">
                    <ClipboardCheck className="mx-auto mb-1 h-6 w-6 text-paris-red-text" />
                    <p className="font-display text-2xl font-bold text-text-primary">{pendingCount}</p>
                    <p className="text-xs text-text-tertiary">Pending Reviews</p>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        );
      })()}

      {/* Manage Projects link card */}
      {user.role === "admin" ? (
        <Link to="/projects">
          <Card className="relative cursor-pointer transition-all duration-75 hover:bg-muted/30 active:scale-[0.98] active:bg-muted/60">
            <span className="absolute right-3 top-3 text-[9px] font-bold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">Soon</span>
            <CardContent className="flex min-h-[56px] items-center justify-between px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
                  <FolderKanban className="h-5 w-5 text-slate-700" />
                </div>
                <div>
                  <p className="text-sm font-medium">Manage Projects</p>
                  <p className="text-xs text-text-tertiary">Create, edit, and manage crews</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-text-tertiary" />
            </CardContent>
          </Card>
        </Link>
      ) : (
        <Card className="relative">
          <span className="absolute right-3 top-3 text-[9px] font-bold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">Soon</span>
          <CardContent className="flex min-h-[56px] items-center justify-between px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
                <FolderKanban className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-tertiary">Manage Projects</p>
                <p className="text-xs text-text-tertiary">Create, edit, and manage crews</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {user.role === "admin" ? (
        <RecentFlhasSection />
      ) : (
        <div>
          <SectionHeader>Recent FLHAs</SectionHeader>
          <Card>
            <CardContent className="flex items-center justify-center py-8">
              <span className="text-[10px] font-bold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded-full">Coming Soon</span>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/** Warns the user if required profile fields are missing, with a link to /profile. */
function ProfileIncompleteWarning() {
  type MeUser = {
    profilePhotoFileId?: string | null;
    phone?: string | null;
    birthDate?: string | null;
    contactEmail?: string | null;
    jobTitleId?: string | null;
    emergencyContactName?: string | null;
    emergencyContactPhone?: string | null;
    emergencyContactRelationship?: string | null;
  };
  const { data } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiGet<{ user: MeUser }>("/api/me"),
    staleTime: 5 * 60_000,
  });

  if (!data?.user) return null;

  const u = data.user;
  const missing: string[] = [];
  if (!u.profilePhotoFileId) missing.push("profile photo");
  if (!u.phone) missing.push("phone number");
  if (!u.birthDate) missing.push("date of birth");
  if (!u.contactEmail) missing.push("contact email");
  if (!u.jobTitleId) missing.push("job title");
  if (!u.emergencyContactName || !u.emergencyContactPhone || !u.emergencyContactRelationship)
    missing.push("emergency contact");

  if (missing.length === 0) return null;

  return (
    <Link to="/profile">
      <div className="rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 flex items-start gap-3 transition-colors hover:bg-red-500/20">
        <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">
            Complete your profile
          </p>
          <p className="text-xs text-red-600/80 dark:text-red-500/80 mt-0.5">
            Missing: {missing.join(", ")}
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
      </div>
    </Link>
  );
}

type NoteResponse = "acknowledged" | "completed" | "need_clarification";

const NOTE_RESPONSE_LABELS: Record<NoteResponse, string> = {
  acknowledged: "Acknowledged",
  completed: "Completed",
  need_clarification: "Need Clarification",
};

const NOTE_RESPONSE_STYLES: Record<NoteResponse, string> = {
  acknowledged: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 ring-blue-200 dark:ring-blue-700",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 ring-green-200 dark:ring-green-700",
  need_clarification: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 ring-amber-200 dark:ring-amber-700",
};

/** Shows system-generated notifications (e.g. division deleted, role changed). */
function SystemNotificationsBanner() {
  const queryClient = useQueryClient();

  type SystemNotif = {
    id: string;
    type: string;
    message: string;
    link: string | null;
    createdAt: string;
  };

  const { data } = useQuery({
    queryKey: ["my-notifications"],
    queryFn: () => apiGet<{ data: SystemNotif[] }>("/api/notifications"),
    staleTime: 10_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const notifications = data?.data ?? [];

  const dismiss = useMutation({
    mutationFn: (id: string) => apiPost(`/api/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-notifications"] }),
  });

  const dismissAll = useMutation({
    mutationFn: () => apiPost("/api/notifications/read-all"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-notifications"] }),
  });

  if (notifications.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
            {notifications.length} system notice{notifications.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => dismissAll.mutate()}
          disabled={dismissAll.isPending}
          className="text-amber-500 hover:text-amber-700 transition-colors disabled:opacity-40"
          aria-label="Dismiss all"
          title="Dismiss all"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-2">
        {notifications.map((n) => {
          const isQuizLocked = n.type === "quiz_locked";
          const inner = (
            <div className={`rounded-lg px-3 py-2.5 flex items-start gap-3 ${isQuizLocked ? "bg-orange-500/10 border border-orange-400/30 cursor-pointer hover:bg-orange-500/15 transition-colors" : "bg-amber-500/10"}`}>
              <div className="flex-1 min-w-0 space-y-0.5">
                <p className="text-[11px] text-amber-600/70 dark:text-amber-400/70">
                  {new Date(n.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  {" · "}
                  {new Date(n.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </p>
                <p className="text-sm text-amber-900 dark:text-amber-100">{n.message}</p>
                {isQuizLocked && (
                  <p className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 mt-0.5">
                    Tap to unlock → Admin Assignments
                  </p>
                )}
                {(n.type === "toolbox_sign" || n.type === "toolbox_ready") && (
                  <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 mt-0.5">
                    {n.type === "toolbox_sign" ? "Tap to sign" : "Tap to review and send"}
                  </p>
                )}
                {n.type === "toolbox_assigned" && (
                  <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 mt-0.5">Tap to start the toolbox</p>
                )}
                {n.type === "toolbox_delegated" && (
                  <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 mt-0.5">Tap to start a toolbox</p>
                )}
                {n.type === "sub_signup" && (
                  <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 mt-0.5">Tap to review and approve</p>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); dismiss.mutate(n.id); }}
                disabled={dismiss.isPending}
                className="text-amber-400 hover:text-amber-600 transition-colors disabled:opacity-40 shrink-0 mt-0.5"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
          if (isQuizLocked) {
            return <Link key={n.id} to="/admin/assignments" search={{ open: n.link ?? undefined, tab: undefined, assignmentId: undefined }}>{inner}</Link>;
          }
          // Take the worker straight to the thing the notice is about.
          if (["doc_assignment", "doc_assignment_cancelled", "doc_assignment_approved", "quiz_unlocked"].includes(n.type)) {
            return <Link key={n.id} to="/documents" search={{ tab: "assignments" }}>{inner}</Link>;
          }
          if (["cert_assignment", "cert_assignment_approved", "cert_assignment_rejected"].includes(n.type)) {
            return <Link key={n.id} to="/my-certifications">{inner}</Link>;
          }
          if (n.type === "toolbox_sign" || n.type === "toolbox_ready") {
            const meetingId = n.link?.match(/\/toolbox\/([^/?#]+)/)?.[1];
            if (meetingId) return <Link key={n.id} to="/toolbox/$meetingId" params={{ meetingId }}>{inner}</Link>;
          }
          if (n.type === "toolbox_assigned") {
            const assignment = n.link?.match(/assignment=([^&]+)/)?.[1];
            if (assignment) return <Link key={n.id} to="/toolbox/new" search={{ assignment }}>{inner}</Link>;
          }
          if (n.type === "toolbox_delegated") {
            return <Link key={n.id} to="/toolbox/new" search={{ edit: undefined }}>{inner}</Link>;
          }
          if (n.type === "sub_signup") {
            return <Link key={n.id} to="/subcontractors" search={{ tab: "requests" }}>{inner}</Link>;
          }
          if (n.type === "cert_assignment_submitted") {
            return <Link key={n.id} to="/admin/assignments" search={{ tab: "certifications", assignmentId: n.link?.match(/assignmentId=([^&]+)/)?.[1], open: undefined }}>{inner}</Link>;
          }
          return <div key={n.id}>{inner}</div>;
        })}
      </div>
    </div>
  );
}

/** Shows unread admin notes for the current worker. Workers respond with Acknowledged / Completed / Need Clarification. */
function AdminNotesBanner() {
  const queryClient = useQueryClient();

  type Note = {
    id: string;
    content: string;
    isReadByWorker: boolean;
    response: NoteResponse | null;
    respondedAt: string | null;
    createdAt: string;
    authorName: string;
  };
  const { data } = useQuery({
    queryKey: ["my-notes"],
    queryFn: () => apiGet<{ data: Note[] }>("/api/me/notes"),
    staleTime: 30_000,
  });

  const unread = (data?.data ?? []).filter((n) => !n.isReadByWorker);

  const markAll = useMutation({
    mutationFn: () => apiPost("/api/me/notes/mark-read"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-notes"] }),
  });

  const respond = useMutation({
    mutationFn: ({ id, response }: { id: string; response: NoteResponse }) =>
      apiPost(`/api/me/notes/${id}/respond`, { response }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-notes"] }),
  });

  if (unread.length === 0) return null;

  return (
    <div className="rounded-xl border border-blue-400/30 bg-blue-500/10 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
          <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
            {unread.length} message{unread.length !== 1 ? "s" : ""} from your admin
          </p>
        </div>
        <Link to="/my-notes" className="ml-auto text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline">
          View all
        </Link>
        <button
          type="button"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending}
          className="text-blue-500 hover:text-blue-700 transition-colors disabled:opacity-40"
          aria-label="Dismiss all"
          title="Dismiss all"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Individual notes */}
      <div className="space-y-2">
        {unread.map((n) => (
          <div key={n.id} className="rounded-lg bg-blue-500/10 px-3 py-2.5 space-y-2">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0 space-y-0.5">
                <p className="text-[11px] text-blue-600/70 dark:text-blue-400/70">
                  {n.authorName}
                  {" · "}
                  {new Date(n.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  {" "}
                  {new Date(n.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </p>
                <p className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-wrap">{n.content}</p>
              </div>
            </div>
            {/* Response buttons */}
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {(["acknowledged", "completed", "need_clarification"] as NoteResponse[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => respond.mutate({ id: n.id, response: r })}
                  disabled={respond.isPending}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-full ring-1 transition-opacity disabled:opacity-40 ${NOTE_RESPONSE_STYLES[r]}`}
                >
                  {NOTE_RESPONSE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Admin-only: surfaces worker "Needs Clarification" responses so they don't get buried in Notes > Sent. */
function NeedsClarificationBanner() {
  const queryClient = useQueryClient();

  type ClarificationNote = {
    id: string;
    content: string;
    respondedAt: string | null;
    createdAt: string;
    workerId: string;
    workerName: string;
    workerEmail: string;
  };

  const { data } = useQuery({
    queryKey: ["admin-notes-needs-clarification"],
    queryFn: () => apiGet<{ data: ClarificationNote[] }>("/api/admin/users/notes/needs-clarification"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const resolve = useMutation({
    mutationFn: (id: string) => apiPost(`/api/admin/users/notes/${id}/resolve`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-notes-needs-clarification"] }),
  });

  const items = data?.data ?? [];
  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
          {items.length} worker{items.length !== 1 ? "s" : ""} need{items.length === 1 ? "s" : ""} clarification
        </p>
      </div>

      <div className="space-y-2">
        {items.map((n) => (
          <div key={n.id} className="rounded-lg bg-amber-500/10 px-3 py-2.5 space-y-2">
            <div className="min-w-0 space-y-0.5">
              <p className="text-[11px] text-amber-600/80 dark:text-amber-400/80">
                {n.workerName}
                {" · "}
                {n.respondedAt
                  ? new Date(n.respondedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  : ""}
              </p>
              <p className="text-sm text-amber-900 dark:text-amber-100 line-clamp-2">{n.content}</p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin/notes"
                search={{ userId: n.workerId }}
                className="text-[11px] font-medium px-2.5 py-1 rounded-full ring-1 ring-amber-300 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-colors"
              >
                Reply to {n.workerName.split(" ")[0]}
              </Link>
              <button
                type="button"
                onClick={() => resolve.mutate(n.id)}
                disabled={resolve.isPending}
                className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full ring-1 ring-emerald-300 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-40"
              >
                <Check className="h-3 w-3" />
                Resolved
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Shows the user's pending document assignments as a structured action list. */
function AssignmentsBanner() {
  type AssignmentItem = {
    ackId: string;
    assignmentId: string;
    doc: { id: string; title: string } | null;
    assigner: { name: string } | null;
    dueDate: string | null;
    message: string | null;
    completionStatus: "incomplete" | "signed" | "awaiting_approval" | "approved";
  };

  const { data } = useQuery({
    queryKey: ["my-doc-assignments"],
    queryFn: () => apiGet<{ data: AssignmentItem[] }>("/api/doc-assignments/mine"),
    staleTime: 2 * 60_000,
  });

  const items = (data?.data ?? []).filter((a) => a.completionStatus !== "approved");
  if (items.length === 0) return null;

  const incomplete = items.filter((a) => a.completionStatus === "incomplete" || a.completionStatus === "signed");
  const awaiting = items.filter((a) => a.completionStatus === "awaiting_approval");

  type Row = { count: number; label: string; sub: string; color: string; dot: string };
  const rows: Row[] = [];
  if (incomplete.length > 0) {
    rows.push({
      count: incomplete.length,
      label: `document assignment${incomplete.length !== 1 ? "s" : ""} assigned to you`,
      sub: incomplete.map((a) => a.doc?.title ?? "Document").join(", "),
      color: "text-orange-700 dark:text-orange-400",
      dot: "bg-orange-500",
    });
  }
  if (awaiting.length > 0) {
    rows.push({
      count: awaiting.length,
      label: `document assignment${awaiting.length !== 1 ? "s" : ""} waiting for approval`,
      sub: awaiting.map((a) => a.doc?.title ?? "Document").join(", "),
      color: "text-blue-700 dark:text-blue-400",
      dot: "bg-blue-500",
    });
  }

  return (
    <Link to="/my-assignments" search={{ tab: "docs" }}>
      <div className="rounded-xl border border-border bg-card px-4 py-3 space-y-2 transition-colors hover:bg-muted/40 cursor-pointer">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pending Actions</p>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        {rows.map((row, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className={`inline-flex items-center justify-center min-w-[28px] h-6 rounded-md text-xs font-bold tabular-nums px-1.5 ${row.dot}/15 ${row.color}`}>
              {row.count}
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${row.color}`}>{row.label}</p>
              <p className="text-xs text-muted-foreground truncate">{row.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </Link>
  );
}
